import { FolderOutlineOpenIcon } from '@tableau/icons/FolderOutlineOpenIcon';
import { ListOddIcon } from '@tableau/icons/ListOddIcon';
import { CheckBoxWidget } from '@tableau/widgets';
import React from 'react';
import { classes } from 'typestyle';

import { Messages } from '~src/codegen/Localize';

import { projectTreeNodeConflictAnnotationClass, projectTreeNodeConflictClass, projectTreeNodeFolderIconClass, projectTreeNodeMoreButtonClass, projectTreeNodeStyle, projectTreeNodeTextStyle } from './RoutingFilterProjectSelectorStyles';

export type ProjectTreeNodeContentProps = Readonly<{
  value: string;
  name: string;
  checked: boolean;
  disabled: boolean;
  readOnly: boolean;
  showNestedActions: boolean;
  // Marks this project as one the server flagged as conflicting; the row gains an
  // error-tinted highlight to match the table's ProjectsInfoPopover treatment.
  isConflict?: boolean;
  onToggle: (value: string) => void;
  onToggleNested: (value: string) => void;
}>;

export const ProjectTreeNodeContent = React.memo(function ProjectTreeNodeContent(props: ProjectTreeNodeContentProps) {
  const { value, name, checked, disabled, readOnly, showNestedActions, isConflict, onToggle, onToggleNested } = props;

  const handleNestedClick = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleNested(value);
  }, [onToggleNested, value]);

  // Give colorblind and screen-reader users a non-color signal for a conflict:
  // a visible "(conflict)" annotation (mirroring ProjectsInfoPopover) plus the
  // semantic aria-invalid state on the checkbox (WCAG 1.4.1). The accessible
  // name stays the plain project name so a locator like getByRole('checkbox',
  // { name }) is stable across locales and conflict state, and so no localized
  // fragment is concatenated into the name (word order/RTL would break).
  const conflictAnnotation = Messages.bridgeSiteSettingsRoutingFilterProjectConflictAnnotation();

  return (
    <div
      className={isConflict ? classes(projectTreeNodeStyle, projectTreeNodeConflictClass) : projectTreeNodeStyle}
      title={name}
      // Non-localized hooks for headless UI tests: data-conflict signals the
      // conflict state and the per-node test id targets a specific project row
      // (rows are otherwise reachable only via the shared [role="treeitem"]).
      data-conflict={isConflict === true}
      data-tb-test-id={'routing-filter-project-node-' + value}
    >
      <CheckBoxWidget
        checked={checked}
        disabled={disabled || readOnly}
        removeLeftMargin={true}
        handleChange={() => onToggle(value)}
        aria-label={name}
        aria-invalid={isConflict === true}
        // Distinct from the row div's test id so the two never share a value.
        // (CheckBoxWidget also appends its own '-CheckBox' suffix on the DOM, so
        // the rendered node is '...-checkbox-CheckBox' — unambiguous either way.)
        testId={'routing-filter-project-node-' + value + '-checkbox'}
      />
      <span className={projectTreeNodeFolderIconClass}><FolderOutlineOpenIcon size={18} /></span>
      <span className={projectTreeNodeTextStyle}>{name}</span>
      {isConflict && <span className={projectTreeNodeConflictAnnotationClass} aria-hidden={true}> {conflictAnnotation}</span>}
      {showNestedActions && (
        <button
          type='button'
          className={projectTreeNodeMoreButtonClass}
          aria-label={Messages.bridgeSiteSettingsRoutingFilterSelectUnselectNestedProjects()}
          title={Messages.bridgeSiteSettingsRoutingFilterSelectUnselectNestedProjects()}
          onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          onClick={handleNestedClick}
        >
          <ListOddIcon size={14} />
        </button>
      )}
    </div>
  );
});
